import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  bulkUpdateChatMessages,
  bulkUpdateConversation,
  createChat,
  createConversation,
  createSms,
  deleteConversation,
  fetchAllConversations,
  updateChat,
  updateConversation,
  updateSelectedMessage,
} from '@store/actions'
import {
  IChatConversation,
  IChatMessage,
  IChatParticipant,
  SendStatus,
} from '@dataTypes/chat.type'
import { logger } from '@utils/safeLogger'
import {
  categorizeConversation,
  mapTwilioToSendStatus,
  sortConversationByDate,
} from '@utils/chat.util'
import {
  ChatResponseData,
  ConversationResponseData,
} from '@services/api/chat.api'

export type ChatState = {
  data: IChatConversation[]
  inbox: IChatConversation[]
  archived: IChatConversation[]
  selectedMessage: IChatConversation | null
  error: string | null
  loading: boolean
}

const initialState: ChatState = {
  data: [],
  inbox: [],
  archived: [],
  selectedMessage: null,
  loading: false,
  error: null,
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    updateSmsSendStatus: (
      state,
      action: PayloadAction<{ message_sid: string; status: string }>,
    ) => {
      const { message_sid, status } = action.payload
      const sendStatus = mapTwilioToSendStatus(status)

      const applyToConversations = (
        conversations: IChatConversation[] | undefined,
      ): void => {
        conversations?.forEach((conv) => {
          conv.chat_message?.forEach((msg) => {
            if (msg.external_id && msg.external_id === message_sid) {
              msg.send_status = sendStatus
            }
          })
        })
      }

      applyToConversations(state.data)
      applyToConversations(state.inbox)
      applyToConversations(state.archived)
      state.selectedMessage?.chat_message?.forEach((msg) => {
        if (msg.external_id && msg.external_id === message_sid) {
          msg.send_status = sendStatus
        }
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAllConversations.fulfilled,
        (state, action: PayloadAction<IChatConversation[]>) => {
          if (!action.payload || !Array.isArray(action.payload)) {
            logger.error('Invalid payload received')
            state.error = 'Invalid response format'
            state.loading = false
            return
          }
          state.data = sortConversationByDate(action.payload)
          const { archived, inbox } = categorizeConversation(state.data)
          state.archived = archived ?? []
          state.inbox = inbox ?? []
          const index = state.data.findIndex(
            (el) =>
              el.chat_conversation_id ===
              state.selectedMessage?.chat_conversation_id,
          )
          if (index !== -1) state.selectedMessage = state.data[index]
          state.loading = false
          state.error = null
        },
      )
      .addCase(fetchAllConversations.pending, (state) => {
        state.data = []
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllConversations.rejected, (state, action) => {
        logger.error('Fetch conversations rejected')
        state.error = action.payload || 'Failed to load conversations'
        state.loading = false
      })

    builder
      .addCase(createChat.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(createChat.pending, (state) => {
        state.loading = true
      })
      .addCase(createChat.rejected, (state) => {
        state.error = 'Failed to create new chat'
        state.loading = false
      })

    builder
      .addCase(
        createSms.fulfilled,
        (state, action: PayloadAction<IChatConversation[]>) => {
          const updatedConversation = sortConversationByDate(action.payload)

          const { archived, inbox } =
            categorizeConversation(updatedConversation)
          state.archived = archived ?? []
          state.inbox = inbox ?? []

          const index = updatedConversation.findIndex(
            (el) =>
              el.chat_conversation_id ===
              state.selectedMessage?.chat_conversation_id,
          )
          if (index !== -1) state.selectedMessage = updatedConversation[index]
          state.data = updatedConversation
          state.loading = false
        },
      )
      .addCase(createSms.pending, (state, action) => {
        if (state.data && state.selectedMessage) {
          const params = action.meta.arg
          const index = state.data.findIndex(
            (el) => el.chat_conversation_id === params.chat_conversation_id,
          )

          if (index !== -1) {
            const chat = state.data[index].chat_message ?? []
            chat.push(params)

            state.data[index].chat_message = chat
            state.selectedMessage.chat_message = chat
            state.data = sortConversationByDate(state.data)

            const { archived, inbox } = categorizeConversation(state.data)
            state.archived = archived ?? []
            state.inbox = inbox ?? []
          }
        }

        state.loading = true
      })
      .addCase(createSms.rejected, (state, action) => {
        const params = action.meta.arg
        const markOptimisticFailed = (
          conversations: IChatConversation[] | undefined,
        ): void => {
          conversations?.forEach((conversation) => {
            if (
              conversation.chat_conversation_id !== params.chat_conversation_id
            ) {
              return
            }
            const queued = [...(conversation.chat_message ?? [])]
              .reverse()
              .find(
                (msg) =>
                  !msg.chat_message_id &&
                  msg.content === params.content &&
                  msg.send_status === SendStatus.queued,
              )
            if (queued) queued.send_status = SendStatus.failed
          })
        }

        markOptimisticFailed(state.data)
        markOptimisticFailed(state.inbox)
        markOptimisticFailed(state.archived)
        if (
          state.selectedMessage?.chat_conversation_id ===
          params.chat_conversation_id
        ) {
          const queued = [...(state.selectedMessage.chat_message ?? [])]
            .reverse()
            .find(
              (msg) =>
                !msg.chat_message_id &&
                msg.content === params.content &&
                msg.send_status === SendStatus.queued,
            )
          if (queued) queued.send_status = SendStatus.failed
        }

        state.error = action.payload || 'Failed to create sms'
        state.loading = false
      })

    builder
      .addCase(
        createConversation.fulfilled,
        (state, action: PayloadAction<ConversationResponseData>) => {
          if (!state.data) return
          const { conversation, participants } = action.payload
          if (conversation && participants) {
            const newConvoParticipant: Partial<IChatConversation> = {
              chat_participant: participants as IChatParticipant[],
            }

            const newConversation = { ...conversation, ...newConvoParticipant }
            const data = [newConversation, ...state.data]

            state.data = sortConversationByDate(data)
            state.selectedMessage =
              state.data.find(
                (d) =>
                  d.chat_conversation_id ===
                  newConversation.chat_conversation_id,
              ) ?? null

            const { archived, inbox } = categorizeConversation(state.data)
            state.archived = archived ?? []
            state.inbox = inbox ?? []
          }
          state.loading = false
          state.error = null
        },
      )
      .addCase(createConversation.pending, (state) => {
        state.loading = true
      })
      .addCase(createConversation.rejected, (state) => {
        state.error = 'Failed to add new conversation'
        state.loading = false
      })

    builder
      .addCase(
        updateChat.fulfilled,
        (state, action: PayloadAction<ChatResponseData>) => {
          if (!state.data) return
          const { chat } = action.payload
          const convoIndex = state.data.findIndex(
            (el) => el.chat_conversation_id === chat?.chat_conversation_id,
          )

          if (convoIndex !== -1 && chat) {
            const conversation = state.data[convoIndex]
            let updatedConversation: IChatConversation

            const chatIndex = conversation.chat_message.findIndex(
              (el) => el.chat_message_id === chat.chat_message_id,
            )
            if (chatIndex !== -1) {
              // Create new array with updated message
              const updatedMessages = [...conversation.chat_message]
              updatedMessages[chatIndex] = chat
              updatedConversation = {
                ...conversation,
                chat_message: updatedMessages,
              }
            } else {
              return
            }

            // Update state.data with new conversation reference
            const updatedData = [...state.data]
            updatedData[convoIndex] = updatedConversation
            state.data = updatedData

            // Update selectedMessage if it matches
            if (
              state.selectedMessage?.chat_conversation_id ===
              updatedConversation.chat_conversation_id
            ) {
              state.selectedMessage = updatedConversation
            }

            // Recalculate inbox and archived lists to ensure UI updates
            const { archived, inbox } = categorizeConversation(updatedData)
            state.archived = archived ?? []
            state.inbox = inbox ?? []
          }
          state.loading = false
          state.error = null
        },
      )
      .addCase(updateChat.pending, (state) => {
        state.loading = true
      })
      .addCase(updateChat.rejected, (state) => {
        state.error = 'Failed to update chat message'
        state.loading = false
      })

    builder
      .addCase(
        updateSelectedMessage.fulfilled,
        (
          state,
          action: PayloadAction<{
            id: string | null
          }>,
        ) => {
          if (!state.data) return

          if (action.payload.id) {
            state.selectedMessage =
              state.data.find(
                (convo) => convo.chat_conversation_id === action.payload.id,
              ) ?? null
          } else {
            state.selectedMessage = null
          }
          state.loading = false
          state.error = null
        },
      )
      .addCase(updateSelectedMessage.pending, (state) => {
        state.loading = true
      })
      .addCase(updateSelectedMessage.rejected, (state) => {
        state.error = 'Failed to update selected message'
        state.loading = false
      })

    builder
      .addCase(
        bulkUpdateConversation.fulfilled,
        (
          state,
          action: PayloadAction<{
            ids: string[]
            data: Partial<IChatConversation>
          }>,
        ) => {
          state.loading = false
          state.error = null
          if (!state.data) return
          state.data = state.data
            .map((convo) => {
              if (
                action.payload.ids.includes(
                  convo.chat_conversation_id as string,
                )
              ) {
                return { ...convo, ...action.payload.data }
              }
              return convo
            })
            .filter((convo) => !convo.is_hidden)
          const { archived, inbox } = categorizeConversation(state.data)
          state.inbox = inbox ?? []
          state.archived = archived ?? []

          if (!state.selectedMessage) return
          state.selectedMessage =
            state.data.find(
              (convo) =>
                convo.chat_conversation_id ===
                state.selectedMessage?.chat_conversation_id,
            ) ?? null
        },
      )
      .addCase(bulkUpdateConversation.pending, (state) => {
        state.loading = true
      })
      .addCase(bulkUpdateConversation.rejected, (state) => {
        state.error = 'Failed to update bulk conversation'
        state.loading = false
      })

    builder
      .addCase(
        bulkUpdateChatMessages.fulfilled,
        (
          state,
          action: PayloadAction<{
            ids: string[]
            data: Partial<IChatMessage>
          }>,
        ) => {
          state.loading = false
          state.error = null
          if (!state.data) return

          // Update chat messages within conversations
          const updatedData = state.data.map((convo) => {
            const updatedMessages = convo.chat_message.map((message) => {
              if (
                action.payload.ids.includes(message.chat_message_id as string)
              ) {
                return { ...message, ...action.payload.data }
              }
              return message
            })

            // Check if any messages were updated in this conversation
            const hasUpdates = updatedMessages.some(
              (msg, idx) => msg !== convo.chat_message[idx],
            )

            return hasUpdates
              ? { ...convo, chat_message: updatedMessages }
              : convo
          })

          state.data = updatedData

          // Update selectedMessage if it contains updated messages
          if (state.selectedMessage) {
            const updatedSelectedMessage = updatedData.find(
              (convo) =>
                convo.chat_conversation_id ===
                state.selectedMessage?.chat_conversation_id,
            )
            if (updatedSelectedMessage) {
              state.selectedMessage = updatedSelectedMessage
            }
          }

          // Recalculate inbox and archived lists
          const { archived, inbox } = categorizeConversation(updatedData)
          state.inbox = inbox ?? []
          state.archived = archived ?? []
        },
      )
      .addCase(bulkUpdateChatMessages.pending, (state) => {
        state.loading = true
      })
      .addCase(bulkUpdateChatMessages.rejected, (state) => {
        state.error = 'Failed to update bulk chat messages'
        state.loading = false
      })

    builder
      .addCase(
        updateConversation.fulfilled,
        (state, action: PayloadAction<IChatConversation[]>) => {
          const sortedData = sortConversationByDate(action.payload)
          state.data = sortedData

          const { archived, inbox } = categorizeConversation(sortedData)
          state.archived = archived ?? []
          state.inbox = inbox ?? []

          if (state.selectedMessage) {
            const index = action.payload.findIndex(
              (el) =>
                el.chat_conversation_id ===
                state.selectedMessage?.chat_conversation_id,
            )
            if (index !== -1) state.selectedMessage = action.payload[index]
          }
          state.loading = false
          state.error = null
        },
      )
      .addCase(updateConversation.pending, (state, action) => {
        state.loading = true

        const params = action.meta.arg

        if (state.data) {
          const index = state.data.findIndex(
            (d) => d.chat_conversation_id === params.id,
          )

          if (index && index !== -1) {
            state.data[index] = { ...state.data[index], ...params.data }
            if (params.id === state.selectedMessage?.chat_conversation_id)
              state.selectedMessage = state.data[index]
            const { archived, inbox } = categorizeConversation(state.data)
            state.archived = archived ?? []
            state.inbox = inbox ?? []
          }
        }
      })
      .addCase(updateConversation.rejected, (state) => {
        state.error = 'Failed to update conversation data'
        state.loading = false
      })

    builder
      .addCase(deleteConversation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        if (!state.data) return
        state.data = state.data.filter(
          (campaign) => campaign.chat_conversation_id !== action.payload,
        )

        const { archived, inbox } = categorizeConversation(state.data)
        state.archived = archived ?? []
        state.inbox = inbox ?? []
      })
      .addCase(deleteConversation.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { updateSmsSendStatus } = chatSlice.actions

export default chatSlice.reducer
